import React, {useEffect, useRef, useState} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import Toast from "cogo-toast";
import moment from "moment/moment";
import DateTimeField from "../../../FinalForm/DatePickerField";
import SelectField from "../../../FinalForm/SelectField";
import {apiGetTurnstile} from "../Turnstiles/Actions";
import {TURNSTILE_TYPE, COMMAND_TYPE_OPTIONS, COMMAND_STATE_OPTIONS} from "../Map/mapConstants";
import {DefaultLoader} from "../../../components/DefaultLoader";
import Filter from "../components/Filter";
import {FilterButton} from "../components/Button";
import PageHeader from "../components/PageHeader";
import CommandTable from "./CommandTable";
import {apiGetCommandList} from "./Actions";
import {
    SET_FILTER_TURNSTILE_COMMAND,
    SET_FILTER_TURNSTILE_COMMAND_RESET,
} from "../../../reducers/filterTurnstileCommand";

const TurnstileCommands = () => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const {user, filterTurnstileCommand} = useSelector(
        (s) => ({user: s.user, filterTurnstileCommand: s.filterTurnstileCommand}), shallowEqual);
    const activeAreal = user?.activeAreal;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [turnstileOptions, setTurnstileOptions] = useState([]);

    const filterData = filterTurnstileCommand?.filterData;

    // Příkazy má v DB jen TICKET_VALIDATOR_SOCKET — nabízet ostatní typy by znamenalo
    // volby, které vždy vrátí prázdný výsledek.
    const loadTurnstileOptions = () => {
        if (!activeAreal) return;
        apiGetTurnstile(activeAreal.id, null, (d) => {
            const list = (d?.turnstile || []).filter(
                (t) => t.turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET);
            setTurnstileOptions(list.map((t) => ({
                value: t.id,
                label: (t.description || ("Turniket #" + t.id)) + (t.uid ? " (" + t.uid + ")" : ""),
            })));
        }, () => setTurnstileOptions([]));
    };

    const getData = (filterValues, nextPage, nextSize = pageSize) => {
        if (!activeAreal) return;
        // Backend ořízne size na MAX_PAGE_SIZE=100 — drž se toho i na FE, ať si
        // stránkování (spočtené z pageSize) neujede od toho, co reálně přišlo.
        const safeSize = Math.min(nextSize, 100);
        setLoading(true);
        apiGetCommandList(activeAreal.id, filterValues, nextPage, safeSize,
            (d) => {
                setData(d ?? null);
                setPage(nextPage);
                setPageSize(safeSize);
                setLoading(false);
            },
            () => { Toast.error("Při načítání příkazů nastala chyba."); setData(null); setLoading(false); });
    };

    useEffect(() => {
        if (activeAreal) {
            loadTurnstileOptions();
            getData(filterData, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAreal]);

    // Backend vrací jen index poslední stránky (maxPage), ne celkový počet záznamů —
    // spočteme si "total", který ETable potřebuje pro stránkování a součet dá stejný
    // počet stránek zpět. Zobrazené "z X" tak je na poslední stránce jen horní odhad.
    const maxPage = data?.maxPage ?? 0;
    const pseudoTotal = (maxPage + 1) * pageSize;

    return (
        <>
            <PageHeader crumbs={[{label: "Spuštěné příkazy"}]}/>
            <Filter
                onSubmit={(values) => {
                    setPage(0);
                    dispatch({type: SET_FILTER_TURNSTILE_COMMAND, payload: values});
                    getData(values, 0);
                }}
                initialValues={filterData}
                formRef={formRef}
                loading={loading}
                extraButtons={({form, submit}) => (
                    <FilterButton
                        onClick={() => {
                            dispatch({type: SET_FILTER_TURNSTILE_COMMAND_RESET});
                            form.reset();
                            submit();
                        }}
                        disabled={loading}
                        text="Zrušit filtr"
                        color="outline"
                    />
                )}
            >
                {({values, submit}) => (
                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px", marginBottom: "8px"}}>
                        <DateTimeField name={"dateFrom"} isMandatory={false} valueToCompare={values?.dateTo}
                            validateFun={(d1, d2) => moment(d1).isAfter(moment(d2)) ? "Datum od nemůže být po datu do" : ""}
                            label={"Datum od"} type={"from"} placeholder={"Datum od"} showTimeSelect onEnter={submit}/>
                        <DateTimeField name={"dateTo"} isMandatory={false} valueToCompare={values?.dateFrom}
                            validateFun={(d1, d2) => moment(d2).isAfter(moment(d1)) ? "Datum od nemůže být po datu do" : ""}
                            label={"Datum do"} type={"to"} placeholder={"Datum do"} showTimeSelect onEnter={submit}/>
                        <SelectField name={"turnstileId"} label={"Turniket"} options={turnstileOptions}
                            placeholder={"Všechny turnikety"}/>
                        <SelectField name={"type"} label={"Typ příkazu"} isMulti options={COMMAND_TYPE_OPTIONS}
                            placeholder={"Všechny typy"}/>
                        <SelectField name={"state"} label={"Stav"} isMulti options={COMMAND_STATE_OPTIONS}
                            placeholder={"Všechny stavy"}/>
                    </div>
                )}
            </Filter>

            {loading && <DefaultLoader/>}
            {!loading && (
                <CommandTable
                    commands={data?.commands}
                    serverMode
                    page={page}
                    pageSize={pageSize}
                    total={pseudoTotal}
                    onPage={(nextPage) => getData(filterData, nextPage)}
                    onPageSize={(nextSize) => getData(filterData, 0, nextSize)}
                />
            )}
        </>
    );
};

export default TurnstileCommands;
